import { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import { Mail, Globe, Phone, MapPin } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";
import { DEFAULT_SETTINGS, fetchSettings } from "@/siteSettings";

export default function Footer() {
  const { t } = useI18n();
  const f = t.footer;
  const [site, setSite] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    fetchSettings().then(setSite);
  }, []);

  return (
    <footer className="bg-pine text-cream">
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-20 sm:px-8">
        <div
          data-testid="footer-brand"
          className="select-none text-center font-serif text-[13vw] leading-none tracking-tight text-transparent lg:text-[9rem]"
          style={{
            backgroundImage: "linear-gradient(180deg, #F6EFE1 20%, rgba(200,127,61,0.7) 80%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
          }}
        >
          MONTE YOLLI
        </div>

        <div className="mt-16 grid gap-10 border-t border-cream/10 pt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <Logo className="h-10 w-10" />
              <span className="font-serif text-lg text-cream">
                Monte <span className="italic text-copperbright">Yolli</span>
              </span>
            </div>
            <p className="mt-4 text-sm font-light leading-relaxed text-cream/70">
              {f.brandDesc}
            </p>
          </div>

          <div>
            <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-copperbright">
              {f.contactTitle}
            </div>
            <div className="flex flex-col gap-3 text-sm font-light text-cream/70">
              <a href={`mailto:${site.contact_email}`} data-testid="footer-email-link" className="flex items-center gap-2 transition-colors hover:text-copperbright">
                <Mail className="h-4 w-4 text-copperbright" /> {site.contact_email}
              </a>
              <a href={`tel:${site.contact_phone.replace(/\s/g, "")}`} data-testid="footer-phone-link" className="flex items-center gap-2 transition-colors hover:text-copperbright">
                <Phone className="h-4 w-4 text-copperbright" /> {site.contact_phone}
              </a>
              <a href={`https://${site.contact_website}`} target="_blank" rel="noopener noreferrer" data-testid="footer-domain-link" className="flex items-center gap-2 transition-colors hover:text-copperbright">
                <Globe className="h-4 w-4 text-copperbright" /> {site.contact_website}
              </a>
            </div>
          </div>

          <div>
            <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-copperbright">
              {f.varietiesTitle}
            </div>
            <div className="flex flex-col gap-2 text-sm font-light text-cream/70">
              {["Geisha", "Gourmet", "Lavado", "Honey"].map((v) => (
                <a key={v} href="#varietes" data-testid={`footer-variety-${v.toLowerCase()}`} className="transition-colors hover:text-copperbright">
                  Monte Yolli — {v}
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-copperbright">
              {f.locationTitle}
            </div>
            <div className="flex items-start gap-2 font-mono text-xs leading-relaxed text-cream/60">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-copperbright" />
              <span>
                {f.location}<br />
                20.08° N — 98.37° O<br />
                {f.altitude}<br />
                {f.format}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-cream/10 pt-8 text-xs font-light text-cream/50 sm:flex-row">
          <span>{f.legal}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
            {f.tagline}
          </span>
        </div>
      </div>
    </footer>
  );
}
